import React, { useCallback, useEffect, useRef, useState } from 'react';
import { inject, observer } from 'mobx-react';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import CircularProgress from '@mui/material/CircularProgress';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import loadGoogleMaps from '../../utils/mapsLoader';
import {
  biasBox, regionCode, distanceKm, validCoordinates,
  extractAddress, matchTenantCity, MAX_DELIVERY_KM
} from '../../utils/geo';
import _useTranslate from '../../hooks/_useTranslate';

/* =====================================================================
   AddressAutocomplete — Google-Places-backed street field
   ---------------------------------------------------------------------
   Used by all three address surfaces (checkout form + the two address
   modals). Built on MUI Autocomplete, which is already a dependency, so
   this adds NO npm package.

   GATING: renders a plain text field — i.e. exactly today's behaviour —
   whenever the tenant has no GOOGLE_MAPS_KEY. Nothing contacts Google in
   that case. Same "never show an affordance that cannot work" choice the
   customer RN app makes (DeliveryAddressA.js:379).

   COST CONTROL (Places Autocomplete is the most expensive surface here and
   is driven by anonymous public traffic):
     - MIN_CHARS before the first request
     - DEBOUNCE_MS between keystrokes
     - ONE session token per address entry, so Google bills one session
       instead of one request per keystroke. The token is discarded after a
       selection, which is what closes the session.

   SAFETY: results are biased to a ~15 km box around the venue and any pick
   farther than MAX_DELIVERY_KM is REFUSED — a courier must never be sent to
   a lookalike street in another prefecture (the admin records a real case
   where a greeklish street resolved confidently 400 km away).
   ===================================================================== */

const MIN_CHARS = 3;
const DEBOUNCE_MS = 350;

const AddressAutocomplete = ({
  feathersStore,
  value = '',
  onInputChange,
  onResolved,
  onRejected,
  error = false,
  helperText = '',
  placeholder = '',
  variant = 'standard',
  disabled = false,
  inputRef,
  onKeyPress,
  fullWidth = true,
  /* `plain` keeps the host page's own input styling (the checkout form uses
     .form-control inputs) while still getting the suggestion dropdown — so the
     street field does not suddenly look like a different design system than the
     fields next to it. */
  plain = false,
  inputClassName = ''
}) => {

  const common = _useTranslate(feathersStore.language, 'common');

  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [notice, setNotice] = useState('');      // our own error (too far / unknown city)

  const placesRef = useRef(null);                // google.maps.places
  const apiModeRef = useRef('auto');             // 'auto' | 'new' | 'legacy'
  const tokenRef = useRef(null);                 // per-entry session token
  const timerRef = useRef(null);
  const mountedRef = useRef(true);

  const enabled = feathersStore.canGeocode() && !disabled;

  useEffect(() => () => { mountedRef.current = false; clearTimeout(timerRef.current); }, []);

  // Load Maps lazily — only for a tenant that has a key, and only once the
  // field is actually rendered, so a customer who never opens an address
  // form costs nothing.
  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    loadGoogleMaps(feathersStore.tenantMapsKey())
      .then(maps => { if (!cancelled && maps) placesRef.current = maps.places; })
      .catch(err => console.log('Maps load failed', err));
    return () => { cancelled = true; };
  }, [enabled, feathersStore.settings?.GOOGLE_MAPS_KEY]);   // eslint-disable-line react-hooks/exhaustive-deps

  const sessionToken = () => {
    const places = placesRef.current;
    if (!places) return undefined;
    if (!tokenRef.current && places.AutocompleteSessionToken) {
      tokenRef.current = new places.AutocompleteSessionToken();
    }
    return tokenRef.current || undefined;
  };

  const fetchSuggestions = useCallback(async (input) => {
    const places = placesRef.current;
    if (!places) return;
    if (apiModeRef.current === 'off') return;   // no Places API on this key
    const settings = feathersStore.settings || {};
    const region = regionCode(settings.country);
    const bias = biasBox(settings.latitude, settings.longitude);

    try {
      setLoading(true);

      // Prefer the NEW Places API: Google no longer enables the legacy Places
      // API for NEW customers, so a tenant registering a fresh Cloud project
      // can only use this one.
      //
      // BUT presence of the class is NOT proof the API is enabled — the JS
      // library always ships AutocompleteSuggestion, and the call then fails
      // at runtime with RpcError "Places API (New) has not been used in
      // project ... before or it is disabled" (observed on AppDate's own
      // project 597039682294). So we probe once and remember the answer,
      // falling back to the legacy service for older projects.
      if (apiModeRef.current !== 'legacy' && places.AutocompleteSuggestion?.fetchAutocompleteSuggestions) {
        const request = {
          input,
          sessionToken: sessionToken(),
          includedRegionCodes: [region],
          language: feathersStore.language || undefined
        };
        if (bias) request.locationBias = bias;
        try {
          const { suggestions } = await places.AutocompleteSuggestion.fetchAutocompleteSuggestions(request);
          if (!mountedRef.current) return;
          apiModeRef.current = 'new';
          setOptions((suggestions || [])
            .map(s => s.placePrediction)
            .filter(Boolean)
            .map(p => ({
              key: p.placeId,
              label: `${p.text?.text ?? p.text ?? ''}`,
              main: `${p.mainText?.text ?? ''}`,
              secondary: `${p.secondaryText?.text ?? ''}`,
              prediction: p
            })));
          return;
        } catch (err) {
          // Not enabled on this project — use the legacy service from now on.
          console.log('Places API (New) unavailable, falling back to the legacy service:', err?.message);
          apiModeRef.current = 'legacy';
        }
      }

      // Legacy path (Places API, pre-2025 projects).
      if (places.AutocompleteService) {
        const service = new places.AutocompleteService();
        const request = {
          input,
          sessionToken: sessionToken(),
          componentRestrictions: { country: region.toLowerCase() },
          language: feathersStore.language || undefined
        };
        if (bias && window.google?.maps?.LatLngBounds) {
          request.bounds = new window.google.maps.LatLngBounds(
            { lat: bias.south, lng: bias.west }, { lat: bias.north, lng: bias.east });
        }
        const predictions = await new Promise(resolve =>
          service.getPlacePredictions(request, (res, status) => {
            // A key with no Places API enabled answers REQUEST_DENIED to every
            // call. Stop asking for the rest of the session: repeating it on
            // each keystroke is pointless and, on a key where it IS billable,
            // wasteful. The field stays usable as a plain text input.
            if (status === 'REQUEST_DENIED' || status === 'OVER_QUERY_LIMIT') {
              console.log('Places autocomplete unavailable for this key:', status);
              apiModeRef.current = 'off';
            }
            resolve(status === 'OK' && res ? res : []);
          }));
        if (!mountedRef.current) return;
        setOptions(predictions.map(p => ({
          key: p.place_id,
          label: p.description,
          main: p.structured_formatting?.main_text || '',
          secondary: p.structured_formatting?.secondary_text || '',
          legacyPlaceId: p.place_id
        })));
      }
    } catch (err) {
      console.log('address autocomplete failed', err);
      if (mountedRef.current) setOptions([]);
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }, [feathersStore]);   // eslint-disable-line react-hooks/exhaustive-deps

  const handleInput = (event, next, reason) => {
    if (reason === 'reset') return;              // fired by our own selection
    onInputChange && onInputChange(next);
    setNotice('');
    if (!enabled) return;
    clearTimeout(timerRef.current);
    const text = `${next ?? ''}`.trim();
    if (text.length < MIN_CHARS) { setOptions([]); return; }
    timerRef.current = setTimeout(() => fetchSuggestions(text), DEBOUNCE_MS);
  };

  // Turn a picked prediction into coordinates + parsed fields.
  const resolvePlace = async (option) => {
    const places = placesRef.current;
    if (!places || !option) return null;

    let location = null, components = null, formatted = '';

    if (option.prediction?.toPlace) {
      const place = option.prediction.toPlace();
      await place.fetchFields({ fields: ['location', 'addressComponents', 'formattedAddress'] });
      location = place.location;
      components = place.addressComponents;
      formatted = place.formattedAddress || option.label;
    } else if (option.legacyPlaceId && places.PlacesService) {
      const service = new places.PlacesService(document.createElement('div'));
      const detail = await new Promise(resolve =>
        service.getDetails({
          placeId: option.legacyPlaceId,
          fields: ['geometry', 'address_components', 'formatted_address'],
          sessionToken: sessionToken()
        }, (res, status) => resolve(status === 'OK' ? res : null)));
      if (!detail) return null;
      location = detail.geometry?.location;
      components = detail.address_components;
      formatted = detail.formatted_address || option.label;
    }

    if (!location) return null;
    const latitude = typeof location.lat === 'function' ? location.lat() : location.lat;
    const longitude = typeof location.lng === 'function' ? location.lng() : location.lng;
    if (!validCoordinates(latitude, longitude)) return null;

    return { latitude, longitude, formatted, ...extractAddress(components) };
  };

  const handleSelect = async (event, option) => {
    if (!option || typeof option === 'string') return;   // freeSolo raw text
    try {
      setLoading(true);
      const resolved = await resolvePlace(option);
      // Selecting closes the Places session: drop the token so the NEXT
      // address entry starts (and is billed as) a new session.
      tokenRef.current = null;
      if (!resolved) { setNotice(common.addressLookupFailed || 'Could not read that address, please type it.'); return; }

      const settings = feathersStore.settings || {};
      // The bias is only a hint — this distance check is the guarantee.
      if (validCoordinates(settings.latitude, settings.longitude)) {
        const km = distanceKm(resolved.latitude, resolved.longitude, settings.latitude, settings.longitude);
        if (km > MAX_DELIVERY_KM) {
          setNotice(common.addressTooFar || 'That address looks too far from the shop — please check it.');
          onRejected && onRejected('tooFar', { ...resolved, km });
          return;
        }
      }

      // Delivery pricing and submission are city-ID based, so prefill the
      // tenant city when the geocoded locality matches one. BEST EFFORT ONLY —
      // never block on a miss: many tenants list delivery ZONES ("Παλιά Πόλη")
      // rather than real localities, and some ship unlabelled city entries, so
      // a hard reject here would refuse perfectly deliverable addresses. The
      // city field is a required dropdown the customer fills anyway; we just
      // ask them to pick it.
      const city = matchTenantCity(
        resolved.cityCandidates, settings.citys,
        (c) => feathersStore._translate(c.name, c.nameEnglish));
      if (!city) setNotice(common.addressPickArea || 'Address found — please pick your area below.');

      onResolved && onResolved({
        street: resolved.street,
        streetNumber: resolved.streetNumber,
        postcode: resolved.postcode,
        cityId: city ? city.id : null,
        cityLabel: city ? city.label : '',
        latitude: resolved.latitude,
        longitude: resolved.longitude,
        formattedAddress: resolved.formatted
      });
      setOptions([]);
    } catch (err) {
      console.log('address resolve failed', err);
      setNotice(common.addressLookupFailed || 'Could not read that address, please type it.');
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  };

  const plainField = (params) => {
    /* MUI puts its OWN ref inside params.inputProps and needs it to drive the
       input (it calls removeAttribute/focus on that node). Spreading inputProps
       and then setting ref={inputRef} clobbered it — with no inputRef passed
       (the checkout case) MUI's ref stayed null and the component threw
       "Cannot read properties of null (reading 'removeAttribute')" on the first
       keystroke. Fork the two refs instead of overwriting. */
    const { ref: muiInputRef, ...restInputProps } = params?.inputProps || {};
    const setRefs = (el) => {
      if (typeof muiInputRef === 'function') muiInputRef(el);
      else if (muiInputRef && typeof muiInputRef === 'object') muiInputRef.current = el;
      if (typeof inputRef === 'function') inputRef(el);
      else if (inputRef && typeof inputRef === 'object') inputRef.current = el;
    };
    return (
      <div ref={params?.InputProps?.ref} style={{ position: 'relative' }}>
        <input
          {...restInputProps}
          ref={setRefs}
          className={inputClassName}
          placeholder={placeholder}
          onKeyPress={onKeyPress}
        />
        {notice ? <div className="error-message">{notice}</div> : null}
      </div>
    );
  };

  const field = (params) => (
    <TextField
      {...params}
      variant={variant}
      placeholder={placeholder}
      error={error || Boolean(notice)}
      helperText={notice || helperText}
      inputRef={inputRef}
      onKeyPress={onKeyPress}
      InputProps={{
        ...(params?.InputProps || {}),
        endAdornment: (
          <>
            {loading ? <CircularProgress color="inherit" size={16} /> : null}
            {params?.InputProps?.endAdornment}
          </>
        )
      }}
    />
  );

  // No tenant key => plain field, no Google, no behaviour change.
  if (!enabled) {
    return plain
      ? <input
          className={inputClassName}
          value={value}
          placeholder={placeholder}
          onKeyPress={onKeyPress}
          ref={inputRef}
          onChange={(e) => onInputChange && onInputChange(e.target.value)}
        />
      : field({
          fullWidth,
          value,
          onChange: (e) => onInputChange && onInputChange(e.target.value)
        });
  }

  return (
    <Autocomplete
      freeSolo                      /* the customer may always type a raw address */
      fullWidth={fullWidth}
      disableClearable
      filterOptions={(x) => x}      /* Google already filtered */
      options={options}
      loading={loading}
      inputValue={value}
      onInputChange={handleInput}
      onChange={handleSelect}
      getOptionLabel={(o) => (typeof o === 'string' ? o : o.label)}
      isOptionEqualToValue={(o, v) => o?.key === v?.key}
      noOptionsText={common.noResults || 'No results'}
      renderOption={(props, option) => (
        <li {...props} key={option.key}>
          <LocationOnIcon fontSize="small" style={{ marginRight: 8, opacity: 0.6 }} />
          <span>
            <strong>{option.main || option.label}</strong>
            {option.secondary ? <span style={{ opacity: 0.7 }}>{` ${option.secondary}`}</span> : null}
          </span>
        </li>
      )}
      renderInput={plain ? plainField : field}
    />
  );
};

export default inject('feathersStore')(observer(AddressAutocomplete));
